import { useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { StickyNote, Loader2, Trash2, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatTimestamp(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

export default function NotesLog({ leadId, legacyNotes, legacyNotesUpdatedAt, entries = [], onChanged }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  // Chronological order (oldest → newest) — earliest at top, latest at bottom
  const sorted = [...entries].sort((a, b) =>
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  const addNote = async () => {
    const value = text.trim();
    if (!value) {
      toast.error("Please type a note");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/leads/${leadId}/notes`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: value }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to add note");
      setText("");
      toast.success("Note added");
      onChanged?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const removeNote = async (noteId) => {
    try {
      const res = await fetch(`${API}/leads/${leadId}/notes/${noteId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Failed to delete");
      }
      toast.success("Note deleted");
      onChanged?.();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const hasAny = legacyNotes || sorted.length > 0;

  return (
    <div className="pt-4 border-t" data-testid="notes-log-section">
      <div className="flex items-center gap-2 mb-3">
        <StickyNote className="w-4 h-4 text-slate-400" />
        <p className="text-sm font-medium text-slate-700">Notes</p>
        {sorted.length > 0 && (
          <span className="text-xs text-slate-400">({sorted.length} entries)</span>
        )}
      </div>

      {/* Legacy single-string notes (preserved for back-compat — shown above the timeline) */}
      {legacyNotes && (
        <div className="mb-3 p-3 rounded-md bg-slate-50 border border-slate-200">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-slate-500 font-medium">General notes</p>
            {legacyNotesUpdatedAt && (
              <p className="text-xs text-slate-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTimestamp(legacyNotesUpdatedAt)}
              </p>
            )}
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{legacyNotes}</p>
        </div>
      )}

      {/* Chronological notes log */}
      {sorted.length > 0 && (
        <ol className="space-y-2 mb-3" data-testid="notes-list">
          {sorted.map((n) => (
            <li
              key={n.note_id}
              className="p-3 rounded-md bg-white border border-slate-200 group"
              data-testid={`note-entry-${n.note_id}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Clock className="w-3 h-3" />
                  <span data-testid="note-timestamp">{formatTimestamp(n.created_at)}</span>
                  {n.created_by_name && (
                    <>
                      <span className="text-slate-300">·</span>
                      <span>{n.created_by_name}</span>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeNote(n.note_id)}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition-opacity"
                  title="Delete note"
                  data-testid={`delete-note-${n.note_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-sm text-slate-800 whitespace-pre-wrap">{n.text}</p>
            </li>
          ))}
        </ol>
      )}

      {!hasAny && (
        <p className="text-sm text-slate-400 italic mb-3">No notes yet. Add the first one below.</p>
      )}

      {/* Composer */}
      <div className="space-y-2">
        <Textarea
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a note… (timestamped on save)"
          data-testid="add-note-input"
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={addNote}
            disabled={saving || !text.trim()}
            data-testid="add-note-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : null}
            Add note
          </Button>
        </div>
      </div>
    </div>
  );
}
